import React, { useContext, useEffect, useState } from 'react';
import Tab from 'react-bootstrap/Tab';
import { useDataRequest } from '@ai4/data-request';
import { CollapsableMenu } from '@ai4/form-viewer';
import PermissionsGroups from './Groups';
import RemovePermissionPopup from './RemovePermissionPopup';
import AddPermissionPopup from './AddPermissionPopup';
import { Permission, Role } from 'src/types';
import { PermissionsContext } from 'src/context';

function PermissionsMain() {
	const { canPermissions } = useContext(PermissionsContext);
	const [activeTab, setActiveTab] = useState<string>();
	const [featureMap, setFeatureMap] = useState<any>();
	const [showRemovePermissionPopup, setShowRemovePermissionPopup] = useState(false);
	const [showAddPermissionPopup, setShowAddPermissionPopup] = useState(false);
	const [selected, setSelected] = useState<any>();
	const { useRestRequest } = useDataRequest();
	const [req] = useRestRequest({
		path: `api/{ver}/features/permissions`,
		method: 'GET',
		jwt: true,
	});

	const load = async () => {
		const feats = await req();
		const map = (feats as any).reduce((acc: any, feat: any) => {
			if (feat.categories === null) return acc;
			if (!acc[feat.moduleName]) acc[feat.moduleName] = {};
			feat.categories.forEach(({ name: categoryName, permissions }: any) => {
				if (!acc[feat.moduleName][categoryName]) acc[feat.moduleName][categoryName] = [];
				acc[feat.moduleName][categoryName] = [...acc[feat.moduleName][categoryName], ...permissions];
			});
			return acc;
		}, {});
		setFeatureMap(map);
		return map;
	};

	useEffect(() => {
		const boot = async () => {
			const map = await load();
			setActiveTab(Object.keys(map)[0]);
		};
		boot();
	}, []);

	const onRemovePermission = (permission: Permission, role: Role) => {
		setShowRemovePermissionPopup(true);
		setSelected({ permission, role });
	};

	const onAddPermission = (permission: Permission) => {
		setShowAddPermissionPopup(true);
		setSelected({ permission });
	};

	const onCloseRemovePopup = () => {
		load();
		setShowRemovePermissionPopup(false);
	};

	const onCloseAddPopup = () => {
		load();
		setShowAddPermissionPopup(false);
	};

	const removePopup = showRemovePermissionPopup ? (
		<RemovePermissionPopup
			selected={selected}
			show={showRemovePermissionPopup}
			onCloseRemovePopup={onCloseRemovePopup}
		/>
	) : null;

	const addPopup = showAddPermissionPopup ? (
		<AddPermissionPopup selected={selected} show={showAddPermissionPopup} onCloseAddPopup={onCloseAddPopup} />
	) : null;

	if (!featureMap) return null;
	return (
		<div>
			<CollapsableMenu
				tabs={Object.keys(featureMap).map((f) => ({
					id: f,
					title: f,
				}))}
				defaultActiveTabId={Object.keys(featureMap)[0]}
				activeTabId={activeTab || ''}
				baseTabId={`${activeTab}-tabpane`}
				onSelect={(k) => setActiveTab(k)}
			/>
			<Tab.Container id='permissions-container' activeKey={activeTab} onSelect={(k) => setActiveTab(k || '')}>
				<Tab.Content>
					{Object.keys(featureMap).map((feature) => {
						return (
							<Tab.Pane
								key={feature}
								id={feature}
								eventKey={feature}
								title={feature}
								role='tabpanel'
								aria-labelledby={`${feature}-tab`}
							>
								<PermissionsGroups
									feature={featureMap[feature]}
									onRemovePermission={onRemovePermission}
									onAddPermission={onAddPermission}
									showAddButton={canPermissions && canPermissions.addToRole}
									showRemoveButton={canPermissions && canPermissions.deleteFromRole}
								/>
							</Tab.Pane>
						);
					})}
				</Tab.Content>
			</Tab.Container>

			{removePopup}
			{addPopup}
		</div>
	);
}

export default PermissionsMain;
